import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Home, Search, Heart, ShoppingCart, User, X, Loader2, Sparkles } from 'lucide-react';
import { useCart } from '@/lib/cart-context';
import { useWishlist } from '@/lib/wishlist-context';
import { useAuth } from '@/lib/auth-context';
import { Input } from '@/components/ui/input';
import { buildPathForLocale } from '@/lib/intl-seo';
import { isExactHomepagePath, requestHomepageRefresh } from '@/lib/homePageVisibility';
import { products, categories } from '@/lib/api';
import { SearchSuggestionsDropdown } from '@/components/layout/SearchSuggestionsDropdown';

const MobileBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { totalItems } = useCart();
  const { count: wishlistCount } = useWishlist();
  const { user } = useAuth();
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [allCategories, setAllCategories] = useState([]);
  const searchPanelRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const cartCount = totalItems || 0;
  const lc = (i18n.language || 'en').substring(0, 2);
  const navPath = useCallback((...segs) => buildPathForLocale(lc, segs), [lc]);

  const matchedCategories = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (q.length < 1 || !allCategories.length) return [];
    return allCategories
      .filter((c) => {
        const name = (c.name || '').toLowerCase();
        const slug = (c.slug || '').toLowerCase();
        return name.includes(q) || slug.includes(q);
      })
      .slice(0, 8);
  }, [searchQuery, allCategories]);

  useEffect(() => {
    let cancelled = false;
    categories
      .list()
      .then((res) => {
        if (!cancelled) {
          const data = res.data;
          setAllCategories(Array.isArray(data) ? data : []);
        }
      })
      .catch(() => {
        if (!cancelled) setAllCategories([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    const trimmed = searchQuery.trim();
    if (!showSearch) {
      setSearchResults([]);
      setShowResults(false);
      setIsSearching(false);
      return () => {};
    }
    if (trimmed.length < 1) {
      setSearchResults([]);
      setShowResults(false);
      setIsSearching(false);
      return () => {};
    }
    const debounceMs = trimmed.length === 1 ? 200 : 140;
    const limit = trimmed.length === 1 ? 6 : 10;
    setIsSearching(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await products.list({
          search: trimmed,
          limit,
          page: 1,
          lang: lc,
          sort: 'created_at',
          order: 'desc',
          autocomplete: true,
        });
        setSearchResults(response.data.products || []);
        setShowResults(true);
      } catch {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, debounceMs);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchQuery, lc, showSearch]);

  useEffect(() => {
    if (!showSearch) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [showSearch]);

  useEffect(() => {
    if (!showSearch) return undefined;
    const onDown = (e) => {
      if (searchPanelRef.current && !searchPanelRef.current.contains(e.target)) {
        setShowSearch(false);
        setSearchQuery('');
        setShowResults(false);
        setSearchResults([]);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showSearch]);

  const navItems = [
    {
      id: 'home',
      icon: Home,
      label: t('nav.home'),
      path: navPath(),
      paths: ['/', navPath()],
    },
    {
      id: 'search',
      icon: Search,
      label: t('nav.search'),
      path: navPath('search'),
      paths: ['/search', navPath('search')],
      action: 'search',
    },
    {
      id: 'wishlist',
      icon: Heart,
      label: t('nav.wishlist'),
      path: '/account/wishlist',
      paths: ['/account/wishlist', navPath('account', 'wishlist')],
      requiresAuth: true,
      badge: wishlistCount,
    },
    {
      id: 'cart',
      icon: ShoppingCart,
      label: t('nav.cart'),
      path: navPath('cart'),
      paths: ['/cart', navPath('cart')],
      badge: cartCount,
    },
    {
      id: 'account',
      icon: User,
      label: t('nav.account'),
      path: user ? navPath('dashboard') : navPath('login'),
      paths: ['/dashboard', navPath('dashboard'), '/login', navPath('login')],
    },
  ];

  const isActive = (item) => {
    if (item.paths) {
      return item.paths.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`));
    }
    return location.pathname === item.path;
  };

  const handleNavClick = (item) => {
    if (item.action === 'search') {
      setShowSearch(true);
    } else if (item.requiresAuth && !user) {
      navigate(navPath('login'));
    } else if (item.id === 'home' && isExactHomepagePath(location.pathname, lc)) {
      requestHomepageRefresh();
    } else {
      navigate(item.path);
    }
  };

  const closeSearch = () => {
    setSearchQuery('');
    setShowSearch(false);
    setShowResults(false);
    setSearchResults([]);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (q) {
      navigate(`${navPath('products')}?search=${encodeURIComponent(q)}`);
      closeSearch();
    }
  };

  const handleAiSearch = () => {
    const q = searchQuery.trim();
    const base = navPath('ai');
    navigate(q.length >= 2 ? `${base}?q=${encodeURIComponent(q)}` : base);
    closeSearch();
  };

  const searchPanelOpen = showResults && searchQuery.trim().length >= 1;
  const qTrim = searchQuery.trim();

  const goProduct = (productId) => {
    navigate(navPath('product', String(productId)));
    setShowSearch(false);
    setSearchQuery('');
    setShowResults(false);
    setSearchResults([]);
  };

  const goCategory = (slug) => {
    if (!slug) return;
    navigate(navPath('category', encodeURIComponent(String(slug))));
    setShowSearch(false);
    setSearchQuery('');
    setShowResults(false);
    setSearchResults([]);
  };

  return (
    <>
      {showSearch && (
        <div
          className="fixed inset-0 bg-white z-[2147483647] sm:hidden flex flex-col min-h-0 overflow-hidden"
          data-testid="mobile-search-panel"
          ref={searchPanelRef}
        >
          <div className="flex items-center gap-2 p-3 border-b bg-white shrink-0">
            <form onSubmit={handleSearchSubmit} className="flex-1 min-w-0">
              <div className="relative flex w-full items-center min-h-[48px] pl-3 pr-1.5 gap-1 bg-background rounded-full border border-neutral-300">
                <Input
                  type="text"
                  inputMode="search"
                  enterKeyHint="search"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  placeholder={t('common.searchPlaceholder', 'Ara')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-12 flex-1 min-w-0 border-0 bg-transparent shadow-none focus-visible:ring-0 text-sm rounded-full px-1"
                  autoFocus
                  data-testid="bottom-nav-search-input"
                />
                {isSearching && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-orange-500" />}
                <button
                  type="submit"
                  className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-neutral-700 hover:bg-neutral-100"
                  aria-label={t('common.search', 'Ara')}
                >
                  <Search className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={handleAiSearch}
                  data-testid="ai-search-btn-mobile"
                  className="inline-flex h-8 shrink-0 items-center gap-1 rounded-full border border-neutral-300 bg-white pl-2 pr-2.5 text-xs font-medium text-neutral-800 hover:bg-neutral-50"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  {t('search.aiButton', 'AI Modu')}
                </button>
              </div>
            </form>
            <button
              type="button"
              onClick={() => {
                setShowSearch(false);
                setSearchQuery('');
                setShowResults(false);
                setSearchResults([]);
              }}
              className="p-2 hover:bg-gray-100 rounded-full flex-shrink-0"
              aria-label={t('common.close', 'Kapat')}
            >
              <X className="w-6 h-6 text-gray-600" />
            </button>
          </div>

          {searchPanelOpen ? (
            <SearchSuggestionsDropdown
              variant="sheet"
              panelOpen={searchPanelOpen}
              queryTrimmed={qTrim}
              isSearching={isSearching}
              matchedCategories={matchedCategories}
              searchResults={searchResults}
              onProductPick={goProduct}
              onCategoryPick={goCategory}
            />
          ) : (
            <div className="flex-1 flex flex-col items-center justify-start pt-12 px-4 text-center min-h-0 overflow-y-auto">
              <p className="text-sm text-muted-foreground mb-6">
                {t('search.mobileTip', 'Yazdıkça ürün ve kategori önerileri görünür')}
              </p>
              <Search className="w-14 h-14 text-muted-foreground/35" aria-hidden />
            </div>
          )}
        </div>
      )}

      <nav
        className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 sm:hidden shadow-lg"
        data-testid="mobile-bottom-nav"
      >
        <div className="flex items-center justify-around h-14 px-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item);

            return (
              <button
                key={item.id}
                onClick={() => handleNavClick(item)}
                className={`flex flex-col items-center justify-center flex-1 h-full py-1 relative transition-colors ${
                  active ? 'text-red-500' : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid={`bottom-nav-${item.id}`}
              >
                <div className="relative">
                  <Icon className={`w-5 h-5 ${active ? 'stroke-[2.5px]' : ''} ${item.id === 'wishlist' && item.badge > 0 ? 'fill-red-500 text-red-500' : ''}`} />
                  {item.badge > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </div>
                <span className={`text-[10px] mt-0.5 ${active ? 'font-semibold' : ''}`}>{item.label}</span>
                {active && <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-red-500 rounded-full" />}
              </button>
            );
          })}
        </div>

        <div className="h-safe-area-inset-bottom bg-white" />
      </nav>
    </>
  );
};

export default MobileBottomNav;
